<template>
  <div class="audio-player">
    <slot
      :is-playing="isPlaying"
      :is-loading="isLoading"
      :is-loading-chapters="isLoadingChapters"
      :chapters="chapters"
      :active-chapter="activeChapter"
      :duration="duration"
      :current-time="currentTime"
      :progress-perc="progressPerc"
      :range-input-bg="rangeInputBg"
      :close-modal="closeModal"
      :is-prev-enabled="isPrevEnabled"
      :is-next-enabled="isNextEnabled"
      :book-name-with-author="bookNameWithAuthor"
      :play="play"
      :pause="pause"
      :get-audiobook-details="getAudiobookDetails"
      :go-to="goTo"
      :on-range-change="onRangeChange"
      :on-range-input="onRangeInput"
      :on-modal-open="onModalOpen"
      :save-listening-history="saveListeningHistory"
    >
    </slot>
    <audio
      ref="audioPlayer"
      :src="activeChapter.mp3"
      preload="none"
      :title="audioTitle"
    ></audio>
  </div>
</template>

<script>
import { storeToRefs } from 'pinia'
import { useBooksProgressStore } from '~/stores/booksProgress'
import { useBooksStore } from '~/stores/books'
import { useUserStore } from '~/stores/user'
import { GENRES_COLORS } from '~/utils/constants'

export const SAFARI_BLOCK_PLAY_ERROR =
  'The request is not allowed by the user agent or the platform in the current context, possibly because the user denied permission.'

export default {
  props: {
    book: {
      type: Object,
      required: true
    },
    genre: {
      type: String,
      required: true
    },
    shouldRequestProgress: {
      type: Boolean,
      default: false
    },
    onErrorCallback: {
      type: Function,
      default: () => {}
    }
  },
  setup() {
    const userStore = useUserStore()
    const booksProgressStore = useBooksProgressStore()
    const { bookProgress } = storeToRefs(booksProgressStore)
    const { getBookProgress, createBookProgress, updateBookProgress } =
      booksProgressStore
    const booksStore = useBooksStore()

    return {
      userStore,
      bookProgressBySlug: bookProgress,
      getAudioBookDownloadUrl: booksStore.getAudioBookDownloadUrl,
      requestBookProgress: getBookProgress,
      createBookProgress,
      updateBookProgress
    }
  },
  data() {
    return {
      isPlaying: false,
      isLoading: false,
      isLoadingChapters: false,
      chapters: [],
      activeChapter: {},
      duration: 0,
      currentTime: 0,
      progressPerc: 0,
      rangeInputBg: GENRES_COLORS[this.genre].light,
      rangeInputDragged: false,
      closeModal: false,
      progressBgColor: GENRES_COLORS[this.genre].light,
      progressColor: GENRES_COLORS[this.genre].bright,
      tokenExpirationTime: null
    }
  },
  computed: {
    loggedInUser() {
      return this.userStore.loggedInUser
    },
    isPrevEnabled() {
      return this.activeChapter && this.activeChapter.order > 1
    },
    isNextEnabled() {
      return (
        this.activeChapter?.order &&
        this.activeChapter.order !== this.chapters.length
      )
    },
    bookNameWithAuthor() {
      return `${this.book.title} - ${this.book.author?.name}`
    },
    audiobook() {
      return this.book.download.audioBook || this.book.download.audiobook
    },
    bookProgress() {
      return this.bookProgressBySlug(this.book.slug)
    },
    audioTitle() {
      if (!this.activeChapter) {
        return this.bookNameWithAuthor
      }

      return `${this.activeChapter.chapter} - ${this.bookNameWithAuthor}`
    }
  },
  mounted() {
    this.setAudioBookProgress()

    const { audioPlayer } = this.$refs
    audioPlayer.onplay = this.onPlay
    audioPlayer.onpause = this.onPause
    audioPlayer.ontimeupdate = this.onTimeUpdate
    audioPlayer.onloadedmetadata = this.onLoadedMetadata
    audioPlayer.onended = this.onEnded
    audioPlayer.onerror = this.onError

    window.addEventListener('beforeunload', this.saveListeningHistory)
  },
  beforeUnmount() {
    this.saveListeningHistory()
    this.pause()
    window.removeEventListener('beforeunload', this.saveListeningHistory)
  },
  methods: {
    getProgressPercentage() {
      return (this.currentTime / this.duration) * 100
    },
    async play() {
      if (this.isLoading) return

      this.isLoading = true

      const audioTokenExpired =
        this.tokenExpirationTime && !this.isAudioTokenValid()

      if (!this.chapters.length || audioTokenExpired) {
        await this.getAudiobookDetails()
      }

      if (this.$refs.audioPlayer && this.chapters.length) {
        try {
          await this.$refs.audioPlayer.play()
          this.pushTrackingEvent()
          this.saveListeningHistory()
          this.setPlaybackState()
        } catch (error) {
          this.onError(error)
        }
      }
    },
    onPlay() {
      if (this.activeChapter?.order) {
        this.isLoading = false
        this.isPlaying = true
      }
    },
    onLoadedMetadata() {
      if (this.$refs.audioPlayer) {
        this.$refs.audioPlayer.currentTime = this.currentTime
      }
    },
    pause() {
      if (this.$refs.audioPlayer && !this.$refs.audioPlayer.paused) {
        this.$refs.audioPlayer.pause()
        this.isPlaying = false
        this.saveListeningHistory()
        this.setPlaybackState()
      }
    },
    onPause() {
      this.isPlaying = false
    },
    onEnded() {
      if (this.activeChapter.order < this.chapters.length) {
        this.goTo(this.activeChapter.order, true)
      }
    },
    onTimeUpdate() {
      if (this.isPlaying && !this.rangeInputDragged && this.$refs.audioPlayer) {
        this.duration = this.$refs.audioPlayer.duration || 0
        this.currentTime = this.$refs.audioPlayer.currentTime || 0
        this.progressPerc = this.getProgressPercentage()
        this.rangeInputBg = this.getRangeInputBg(this.progressPerc)
      }
    },
    onError(event) {
      this.isPlaying = false
      this.isLoading = false
      this.isLoadingChapters = false

      if (event?.message === SAFARI_BLOCK_PLAY_ERROR) {
        return this.$toast.success(this.$label('books.audioLoaded'))
      }
      this.closeModal = true

      const isAbortError = event?.name === 'AbortError'
      if (!isAbortError) {
        this.$toast.error(this.$label('books.audioPlayerError'))
        this.onErrorCallback()
        const mediaError = event.target?.error
        const error = mediaError
          ? `MediaError code: ${mediaError.code}; MediaError message: ${mediaError.message}`
          : event

        this.$sentryCaptureException(error)
      }
    },
    async getAudiobookDetails() {
      this.isLoadingChapters = true

      try {
        const response = await this.getAudioBookDownloadUrl({
          id: this.audiobook.id,
          categoryCode: this.book.categoryCode,
          format: 'mp3',
          language: 'nl-NL',
          isPlayer: true
        })

        if (!response.chapters || !response.chapters.length)
          throw new Error('No chapters available for this book')

        this.chapters = response.chapters
        const chapterNum = this.activeChapter.order
          ? this.activeChapter.order - 1
          : 0

        this.activeChapter = this.chapters[chapterNum]
        this.setTokenExpirationTime(response.audioFilesExpiryTimeInUTC)
        this.setMetadata()
      } catch (error) {
        this.onError(error)
      } finally {
        this.isLoadingChapters = false
      }
    },
    async goTo(chapter, autoplay) {
      if (!this.chapters.length) await this.getAudiobookDetails()

      this.isPlaying = false
      this.activeChapter = this.chapters[chapter]
      this.rangeInputBg = this.progressBgColor
      this.progressPerc = 0
      this.duration = 0
      this.currentTime = 0

      if (this.$refs.audioPlayer) {
        await this.$refs.audioPlayer.load()
      }

      this.setMetadata()

      if (autoplay) this.play()
    },
    onRangeChange(event) {
      if (!this.duration) {
        this.progressPerc = 0
      } else {
        this.progressPerc = parseInt(event.target.value)
        this.$refs.audioPlayer.currentTime =
          (event.target.value / 100) * this.duration
      }

      this.rangeInputDragged = false
    },
    onRangeInput(event) {
      this.progressPerc = parseInt(event.target.value)
      this.currentTime = (event.target.value / 100) * this.duration

      if (this.duration) {
        this.rangeInputBg = this.getRangeInputBg(event.target.value)
      }

      this.rangeInputDragged = true
    },
    getRangeInputBg(percent) {
      return `linear-gradient(to right, ${this.progressColor} 0%, ${this.progressColor} ${percent}%, ${this.progressBgColor} ${percent}%, ${this.progressBgColor} 100%)`
    },
    setMetadata() {
      if ('mediaSession' in navigator && this.activeChapter) {
        navigator.mediaSession.metadata = new window.MediaMetadata({
          title: this.activeChapter.chapter,
          artist: this.bookNameWithAuthor,
          artwork: [{ src: this.book.cover }]
        })

        this.setMediaSessionHandlers()
      }
    },
    mediaSessionSeekTo(details) {
      this.$refs.audioPlayer.currentTime = details.seekTime
      this.currentTime = details.seekTime
      this.setPositionState()
    },
    setMediaSessionHandlers() {
      navigator.mediaSession.setActionHandler('play', this.play)
      navigator.mediaSession.setActionHandler('pause', this.pause)

      const prevTrackHandler = this.isPrevEnabled
        ? () => this.goTo(this.activeChapter.order - 2, true)
        : undefined

      navigator.mediaSession.setActionHandler('previoustrack', prevTrackHandler)

      const nextTrackHandler = this.isNextEnabled
        ? () => this.goTo(this.activeChapter.order, true)
        : undefined

      navigator.mediaSession.setActionHandler('nexttrack', nextTrackHandler)

      navigator.mediaSession.setActionHandler('seekto', this.mediaSessionSeekTo)
    },
    setPositionState() {
      try {
        navigator.mediaSession.setPositionState({
          duration: this.$refs.audioPlayer.duration,
          playbackRate: 1,
          position: this.currentTime
        })
        // eslint-disable-next-line no-empty
      } catch {}
    },
    setPlaybackState() {
      try {
        const playbackState = this.isPlaying ? 'playing' : 'paused'
        navigator.mediaSession.playbackState = playbackState
        this.setPositionState()
        // eslint-disable-next-line no-empty
      } catch {}
    },
    setTokenExpirationTime(expDate) {
      if (expDate) {
        const [datePart, timePart] = expDate.split(' ')
        const [day, month, year] = datePart.split('/')
        const [hours, minutes, seconds] = timePart.split(':')

        this.tokenExpirationTime = new Date(
          Date.UTC(
            parseInt(year),
            parseInt(month) - 1,
            parseInt(day),
            parseInt(hours),
            parseInt(minutes),
            parseInt(seconds)
          )
        )
      }
    },
    isAudioTokenValid() {
      if (this.tokenExpirationTime) {
        const dateNow = new Date()
        const expDate = this.tokenExpirationTime.getTime()
        const dateNowPlus3Hours = new Date(
          dateNow.getTime() + 3 * 60 * 60 * 1000
        )

        return dateNowPlus3Hours.getTime() < expDate
      }
      return false
    },
    async setAudioBookProgress() {
      try {
        if (this.shouldRequestProgress) {
          await this.requestBookProgress({
            userId: this.loggedInUser.id,
            bookSlug: this.book.slug
          })
        }

        if (this.bookProgress && this.bookProgress.chapterListenedSeconds) {
          this.activeChapter = {
            chapter: this.bookProgress.chapterTitle,
            duration: this.bookProgress.chapterDuration,
            order: this.bookProgress.chapter
          }

          this.duration = this.activeChapter.duration
          this.currentTime = this.bookProgress.chapterListenedSeconds
          this.progressPerc = this.getProgressPercentage()
          this.rangeInputBg = this.getRangeInputBg(this.progressPerc)
        }
      } catch (error) {
        this.$sentryCaptureException(error)
        this.$toast.error(this.$label('books.progress.unableToRetrieveError'))
      }
    },
    async saveListeningHistory() {
      try {
        if (!this.loggedInUser || !this.chapters.length || !this.activeChapter)
          return

        const progressData = {
          chapter: this.activeChapter.order,
          listenedSeconds: this.getTotalTimeListened(),
          chapterListenedSeconds: this.currentTime,
          chapterTitle: this.activeChapter.chapter,
          chapterDuration: this.duration
        }

        const bookProgress = this.bookProgressBySlug(this.book.slug)

        if (!bookProgress) {
          await this.createBookProgress({
            ...progressData,
            bookSlug: this.book.slug,
            userId: this.loggedInUser.id
          })
        } else {
          await this.updateBookProgress({
            ...progressData,
            id: bookProgress.id
          })
        }
      } catch (error) {
        this.$sentryCaptureException(error)
        this.$toast.error(this.$label('books.progress.unableToSaveError'))
      }
    },
    getTotalTimeListened() {
      let time = 0

      for (let i = 0; i < this.activeChapter.order - 1; i++) {
        time += this.chapters[i].duration
      }

      time += this.currentTime

      return time
    },
    pushTrackingEvent() {
      this.$gtmTracking.push({
        event: 'interact_event',
        eventAction: 'play',
        eventCategory: 'MonthlyBooksDetails || audioPlayer',
        eventLabel: this.book.title
      })
    },
    onModalOpen() {
      this.closeModal = false
    }
  }
}
</script>
